import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';

// Helper function to safely convert Firestore Timestamps or date strings to JS Date objects
const toDateSafe = (timestamp) => {
    if (!timestamp) return null;
    if (typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
    }
    // Handle cases where the date might already be a string from previous state
    if (typeof timestamp === 'string' && !isNaN(new Date(timestamp))) {
        return new Date(timestamp);
    }
    return null;
};

// New helper function to format date differences professionally
const formatDateDifference = (date1, date2) => {
    if (!date1 || !date2) return "N/A";
    const diffTime = Math.abs(date2 - date1);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "on the same day";

    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30.44); // Average days in a month
    const days = Math.round((diffDays % 365) % 30.44);

    let parts = [];
    if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
    if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
    if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);

    return parts.join(', ') + " apart";
};


const PriceComparison = ({ user, currentProduct, linkedProductId, onClose }) => {
  const [linkedProduct, setLinkedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState('');
  const [timelineDiff, setTimelineDiff] = useState(null);

  useEffect(() => {
    const fetchLinkedProduct = async () => {
      if (!user || !linkedProductId) {
        setError('Linked Product ID is missing or not provided.');
        setLoading(false);
        return;
      }
      
      const q = query(
        collection(db, 'products'),
        where('userId', '==', user.uid),
        where('productId', '==', linkedProductId),
        limit(1)
      );

      try {
        const querySnapshot = await getDocs(q);
        if (querySnapshot.empty) {
          setError(`Could not find a product with ID: ${linkedProductId}`);
        } else {
          const linkedData = querySnapshot.docs[0].data();
          setLinkedProduct(linkedData);
          generateSummary(currentProduct, linkedData);

          const currentShipDate = toDateSafe(currentProduct.vendorShippingDate);
          const currentReceiveDate = toDateSafe(currentProduct.receivedDate);
          const linkedShipDate = toDateSafe(linkedData.vendorShippingDate);
          const linkedReceiveDate = toDateSafe(linkedData.receivedDate);

          if(currentShipDate && currentReceiveDate && linkedShipDate && linkedReceiveDate) {
            const currentDuration = (currentReceiveDate - currentShipDate) / (1000 * 60 * 60 * 24);
            const linkedDuration = (linkedReceiveDate - linkedShipDate) / (1000 * 60 * 60 * 24);
            setTimelineDiff({
              current: currentDuration,
              linked: linkedDuration,
              diff: currentDuration - linkedDuration,
            });
          }
        }
      } catch (err) {
        console.error("Error fetching linked product:", err);
        setError("An error occurred while fetching the linked product. A new index may be required. Check the console for a link.");
      }
      setLoading(false);
    };

    fetchLinkedProduct();
  }, [user, linkedProductId, currentProduct]);

  const generateSummary = (current, linked) => {
      const perItemDiff = (current.pricePerItem || 0) - (linked.pricePerItem || 0);
      const shippingDiff = (current.vendorShippingCost || 0) - (linked.vendorShippingCost || 0);
      const agentDiff = (current.agentShippingCost || 0) - (linked.agentShippingCost || 0);

      const differences = [
          { name: 'Price Per Item', value: perItemDiff },
          { name: 'Vendor Shipping', value: shippingDiff },
          { name: 'Agent Shipping', value: agentDiff },
      ].filter(d => d.value !== 0);

      if (differences.length === 0) {
          setSummary('There are no significant cost differences between these two orders.');
          return;
      }

      differences.sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
      const biggestChange = differences[0];
      const direction = biggestChange.value > 0 ? 'increased' : 'decreased';
      const summaryText = `The main cost difference is in **${biggestChange.name}**, which ${direction} by **${current.currency} ${Math.abs(biggestChange.value).toFixed(2)}**. This had the largest impact on the final per-unit cost.`;
      setSummary(summaryText);
  };
  
  const renderRow = (label, currentVal, linkedVal, currency = '') => {
    const currentValue = parseFloat(currentVal) || 0;
    const linkedValue = parseFloat(linkedVal) || 0;
    const difference = currentValue - linkedValue;
    
    let diffColor = 'var(--text-secondary)';
    let sign = '';
    if (difference > 0.01) { diffColor = 'var(--brand-danger)'; sign = '+'; }
    if (difference < -0.01) { diffColor = 'var(--brand-success)'; }

    return (
      <tr key={label}>
        <td>{label}</td>
        <td>{currency}{currentValue.toFixed(2)}</td>
        <td>{currency}{linkedValue.toFixed(2)}</td>
        <td style={{ color: diffColor, fontWeight: 'bold' }}>{sign}{difference.toFixed(2)}</td>
      </tr>
    );
  };
  
  const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-GB') : 'N/A';

  if (loading) return <div className="form-section"><p>Loading comparison data...</p></div>;
  if (error) return <div className="form-section"><p style={{color: 'var(--brand-danger)'}}>{error}</p><button className="cancel-button" onClick={onClose}>Close</button></div>;
  if (!linkedProduct) return null;

  return (
    <div className="comparison-modal-backdrop">
      <div className="comparison-modal-content">
          <div className="page-header">
              <h2>Price Comparison</h2>
              <button onClick={onClose} className="close-button">&times;</button>
          </div>
          <div className="comparison-summary-card">
              <h3>Smart Summary</h3>
              <p dangerouslySetInnerHTML={{ __html: summary.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
          </div>
          <div className="comparison-grid">
              <div className="form-section">
                  <h4>Current: {currentProduct.productId}</h4>
                  <div className="spec-field-grid">
                      <div className="spec-field"><strong>Purchase Date:</strong> {formatDate(toDateSafe(currentProduct.purchaseDate))}</div>
                      <div className="spec-field"><strong>Unit Cost:</strong> RM {currentProduct.perUnitCostMYR.toFixed(2)}</div>
                  </div>
              </div>
              <div className="form-section">
                  <h4>Linked: {linkedProduct.productId}</h4>
                  <div className="spec-field-grid">
                      <div className="spec-field"><strong>Purchase Date:</strong> {formatDate(toDateSafe(linkedProduct.purchaseDate))}</div>
                      <div className="spec-field"><strong>Unit Cost:</strong> RM {linkedProduct.perUnitCostMYR.toFixed(2)}</div>
                  </div>
              </div>
          </div>
          <div className="form-section">
              <h3>Cost Breakdown</h3>
              <div className="table-wrapper">
                  <table className="product-list-table comparison-table">
                      <thead><tr><th>Metric</th><th>Current Order</th><th>Linked Order</th><th>Difference</th></tr></thead>
                      <tbody>
                          {renderRow('Price Per Item', currentProduct.pricePerItem, linkedProduct.pricePerItem, `${currentProduct.currency} `)}
                          {renderRow('Vendor Shipping', currentProduct.vendorShippingCost, linkedProduct.vendorShippingCost, `${currentProduct.currency} `)}
                          {currentProduct.vendorShippingMethod === 'Agent' && renderRow('Agent Shipping', currentProduct.agentShippingCost, linkedProduct.agentShippingCost, `${currentProduct.currency} `)}
                          <tr className="divider-row"><td colSpan="4"></td></tr>
                          {renderRow('Per Unit Cost', currentProduct.perUnitCostMYR, linkedProduct.perUnitCostMYR, 'RM ')}
                      </tbody>
                  </table>
              </div>
          </div>
          
          <div className="form-section">
                <h3>Timeline Analysis</h3>
                <div className="spec-field-grid">
                    <div className="spec-field">
                        <strong>Purchase Interval:</strong>
                        <span>This order was placed **{formatDateDifference(toDateSafe(currentProduct.purchaseDate), toDateSafe(linkedProduct.purchaseDate))}**.</span>
                    </div>
                    {timelineDiff && (
                        <div className="spec-field" style={{backgroundColor: timelineDiff.diff > 0 ? 'var(--bg-accent-yellow)' : 'var(--bg-accent-green)'}}>
                            <strong>Shipping Duration:</strong> 
                            <span style={{color: timelineDiff.diff > 0 ? 'var(--brand-warning)' : 'var(--brand-success)'}}>
                                This order took **{timelineDiff.diff > 0 ? `+${timelineDiff.diff.toFixed(0)}` : timelineDiff.diff.toFixed(0)} days** longer to arrive than the linked order.
                            </span>
                        </div>
                    )}
                </div>
            </div>

      </div>
    </div>
  );
};

export default PriceComparison;
